import type { AuthSession, User } from '@voll-intelligence/domain';
import type { CognitoService, CognitoTokens } from './cognito_service';
import {
  readTokens,
  writeTokens,
  readGeneration,
  readTombstone,
  REFRESH_LEEWAY_MS,
} from './auth_tokens';
import { authSync } from './auth_sync';

interface CompleteLoginResult {
  user: User;
  session: AuthSession;
  returnTo: string | null;
}

export interface AuthService {
  beginLogin: (opts?: { returnTo?: string }) => Promise<void>;
  completeLogin: () => Promise<CompleteLoginResult>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

export function createAuthService(cognitoService: CognitoService): AuthService {
  let inflight: Promise<string | null> | null = null;

  async function beginLogin(opts?: { returnTo?: string }): Promise<void> {
    await cognitoService.beginSsoLogin(opts);
  }

  async function completeLogin(): Promise<CompleteLoginResult> {
    const { tokens, user, session, returnTo } = await cognitoService.exchangeCodeForTokens();
    writeTokens(tokens);
    authSync.emitLogin(); // mints generation, clears tombstone, broadcasts
    return { user, session, returnTo };
  }

  async function signOut(): Promise<void> {
    try {
      await cognitoService.signOut();
    } finally {
      writeTokens(null);
      authSync.emitSignout(); // sets tombstone + clears generation + broadcasts
    }
  }

  function doRefresh(prev: CognitoTokens): Promise<string | null> {
    inflight ??= (async () => {
      const attemptedGeneration = readGeneration();
      try {
        const refreshed = await cognitoService.refreshTokens(prev);
        // Abort if signed out during the await. `>=` mirrors the coordinator's
        // adoption rule (accept only when generation > tombstone) and closes the
        // same-millisecond Date.now() tie. In doRefresh attemptedGeneration is
        // always > 0 (a session exists), and emitLogin clears the tombstone to 0,
        // so a live same-session refresh (tombstone 0) always proceeds.
        if (readTombstone() >= attemptedGeneration) return null; // signed out mid-refresh
        writeTokens(refreshed);
        authSync.emitRefresh();
        return refreshed.accessToken;
      } catch {
        if (readTombstone() >= attemptedGeneration) return null; // already signed out
        const current = readTokens();
        if (current && current.accessToken !== prev.accessToken) return current.accessToken; // newer set arrived
        writeTokens(null);
        return null;
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  }

  async function getAccessToken(): Promise<string | null> {
    const tokens = readTokens();
    if (!tokens) return null;
    if (tokens.expiresAt > Date.now() + REFRESH_LEEWAY_MS) {
      return tokens.accessToken;
    }
    if (!tokens.refreshToken) {
      writeTokens(null);
      return null;
    }
    return doRefresh(tokens);
  }

  return { beginLogin, completeLogin, signOut, getAccessToken };
}
